import React from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { OverrideList, OverrideEdit } from './Override.js';
import { ScheduleList, ScheduleEdit, ScheduleCreate } from './Schedule.jsx';
import RelaisLogPage from '../pages/RelaisLogPage.jsx';
import PriceDataPage from '../pages/PriceDataPage.jsx';
import SettingsPage from '../pages/SettingsPage';
import SimulationPage from '../pages/SimulationPage';
import HydrogenPricePage from '../pages/HydrogenPricePage';
import ElectricityPricePage from '../pages/ElectricityPricePage';
import EnvironmentManagementPage from '../pages/EnvironmentManagementPage';
import UserManagementPage from '../pages/UserManagementPage';
import DashboardPage from '../pages/DashboardPage';
import WelcomePage from '../pages/WelcomePage';
import AccountSettingsPage from '../pages/AccountSettingsPage';
import SystemAuditPage from '../pages/SystemAuditPage';
import Layout from './Layout';
import Login from '../pages/Login';
import EnvironmentSelector from './Environment/EnvironmentSelector';
import { useAuth } from '../contexts/AuthContext';
import { isMultiTenantMode } from '../config/api';

const LoadingScreen = ({ message = 'Laden...' }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      gap: 2
    }}
  >
    <CircularProgress size={60} />
    <Typography variant="h6" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const ElectrolyzerAdmin = ({ dataProvider, i18nProvider, isMultiTenant = false }) => {
  const { selectedEnvironment } = useAuth();
  
  return (
    <Admin 
      dataProvider={dataProvider}
      i18nProvider={i18nProvider}
      layout={Layout} // Gebruik onze custom Layout binnen React Admin
      disableTelemetry
      requireAuth={false} // Auth wordt al gehandled door MultiTenantApp
    >
      <Resource name="control" list={OverrideList} edit={OverrideEdit} />
      <Resource name="logbook" list={RelaisLogPage} />
      <Resource name="marketdata" list={PriceDataPage} />
      <Resource name="planning" list={ScheduleList} edit={ScheduleEdit} create={ScheduleCreate} />
      <Resource name="simulation" list={SimulationPage} />
      <Resource name="settings" list={SettingsPage} />
      {isMultiTenant && <Resource name="environments" list={EnvironmentManagementPage} />}
      <CustomRoutes>
        <Route path="/" element={isMultiTenant && selectedEnvironment ? <DashboardPage /> : <DashboardPage />} />
        <Route path="/hydrogen-price" element={<HydrogenPricePage />} />
        <Route path="/electricity-price" element={<ElectricityPricePage />} />
        {isMultiTenant && <Route path="/user-management" element={<UserManagementPage />} />}
        {isMultiTenant && <Route path="/account-settings" element={<AccountSettingsPage />} />}
        {isMultiTenant && <Route path="/system-audit" element={<SystemAuditPage />} />}
      </CustomRoutes>
    </Admin>
  );
};

const MultiTenantApp = ({ dataProvider, i18nProvider, theme }) => {
  const { 
    isAuthenticated, 
    selectedEnvironment, 
    loading,
    user,
    environments
  } = useAuth();
  
  const [showEnvironmentManagement, setShowEnvironmentManagement] = React.useState(false);

  // Loading state (alleen in multi-tenant mode)
  if (loading && isMultiTenantMode()) {
    return <LoadingScreen message="Authenticatie controleren..." />;
  }

  // Single tenant mode: skip auth en environment checks, ga direct naar admin
  if (!isMultiTenantMode()) {
    return (
      <ElectrolyzerAdmin 
        dataProvider={dataProvider} 
        i18nProvider={i18nProvider}
        isMultiTenant={false}
      />
    );
  }

  // Multi-tenant mode: check auth and environment selection
  // Stap 1: Niet ingelogd - toon moderne login met lokaal + OAuth
  if (!isAuthenticated || !user) {
    return <Login />;
  }

  // Stap 2: Ingelogd maar geen environment geselecteerd
  if (!selectedEnvironment) {
    // Als de gebruiker geen environments heeft, of expliciet management wil
    if ((environments && environments.length === 0) || showEnvironmentManagement) {
      return (
        <ElectrolyzerAdmin 
          dataProvider={dataProvider} 
          i18nProvider={i18nProvider}
          isMultiTenant={true}
        />
      );
    }
    // Anders toon environment selector
    return (
      <EnvironmentSelector 
        onManageEnvironments={() => setShowEnvironmentManagement(true)}
      />
    );
  }

  // Stap 3: Volledig geauthenticeerd en environment geselecteerd - toon admin interface
  return (
    <ElectrolyzerAdmin 
      dataProvider={dataProvider} 
      i18nProvider={i18nProvider}
      isMultiTenant={true}
    />
  );
};

export default MultiTenantApp; 